// O cliente da API. Tudo que o webmail fala com o servidor passa por aqui.
//
// Três responsabilidades, e nenhuma a mais:
//
//   1. montar a requisição (base, bearer, JSON)
//   2. transformar resposta de erro em ErroDaAPI tipado
//   3. renovar a sessão quando o token expira, UMA vez, sem estouro
//
// Não decide o que mostrar, não guarda cache (isso é do react-query) e não
// conhece tela nenhuma.

import { deposito } from "./deposito";
import { caminhoDeRenovacao, guardarVagaDaAba } from "./vaga";
import { ErroDaAPI, ErroDeRede, type Problema } from "./problema";

/**
 * Caminho relativo de propósito: o nginx serve o webmail e a API na MESMA
 * origem (webmail.jhou.tec.br). Sem origem cruzada não há CORS para
 * configurar, nem preflight a cada requisição — e, quando o refresh virar
 * cookie httpOnly, ele já funciona sem mais nada.
 */
const BASE = "/api/v1";

/** Rotas que nunca disparam renovação — senão 401 no refresh chamaria refresh. */
const SEM_RENOVACAO = new Set(["/auth/login", "/auth/refresh", "/auth/2fa/verificar"]);

type Metodo = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface Opcoes {
  corpo?: unknown;
  consulta?: Record<string, string | number | boolean | undefined | null>;
  sinal?: AbortSignal;
  /** Resposta binária (anexo). Devolve Blob em vez de JSON. */
  binario?: boolean;
}

/** Chamado quando a sessão acaba de vez. A aplicação manda para o login. */
let aoPerderSessao: () => void = () => {};
export function quandoPerderSessao(f: () => void): void {
  aoPerderSessao = f;
}

/**
 * Por que a sessão caiu, quando dá para saber. A tela de entrar mostra, e o
 * silêncio é a alternativa ruim: quem é desconectado sem explicação conclui
 * que o webmail quebrou.
 */
let motivo: string | null = null;
export function motivoDaPerda(): string | null {
  return motivo;
}
export function esquecerMotivo(): void {
  motivo = null;
}

export const AVISO_PERSONIFICACAO_EXPIROU =
  "O acesso à caixa do cliente expirou — ele dura 30 minutos. " +
  "Entre de novo para voltar ao painel.";

export const AVISO_OUTRA_CONTA =
  "Esta aba foi desconectada porque outra conta entrou neste navegador. " +
  "Para usar duas contas ao mesmo tempo, abra a segunda numa janela anônima " +
  "ou em outro perfil do navegador.";

/**
 * A RENOVAÇÃO SÓ VALE PARA QUEM PEDIU.
 *
 * O cookie de renovação tem um nome por ORIGEM, então duas contas abertas no
 * mesmo endereço disputam o mesmo cookie: a segunda a entrar sobrescreve a
 * primeira. Quando a primeira aba renova, o servidor — corretamente — devolve
 * a identidade do cookie que recebeu, que agora é de outra pessoa.
 *
 * Reproduzido em 26/10/2026 com duas caixas de laboratório. Sem esta
 * comparação, a aba adotava o token alheio e passava a mostrar a caixa de
 * outra pessoa SOB O NOME DA PRIMEIRA — a forma mais cara de errar deste
 * produto, porque não parece quebrado.
 *
 * `esperada` nulo é retomada de página: a aba ainda não sabe quem é, e quem
 * manda é o cookie. Recusar aí deslogaria todo mundo a cada F5.
 */
export function renovacaoEhDaMesmaConta(
  esperada: string | null,
  recebida: string | null | undefined,
): boolean {
  if (!esperada) return true;
  if (!recebida) return true; // servidor antigo, sem o campo: não inventa recusa
  return esperada.toLowerCase() === recebida.toLowerCase();
}

/**
 * Renovação em voo, compartilhada.
 *
 * Um webmail dispara várias requisições ao mesmo tempo — lista, contadores,
 * pastas. Com o token expirado, TODAS levam 401 juntas. Sem esta trava, cada
 * uma chamaria /auth/refresh, e como o refresh é rotativo (cada uso invalida o
 * anterior), a segunda derrubaria a sessão que a primeira acabou de renovar.
 * O usuário seria deslogado exatamente por ter várias abas abertas.
 */
let renovacaoEmVoo: Promise<boolean> | null = null;

async function renovar(): Promise<boolean> {
  try {
    // Sem corpo: o refresh viaja no cookie HttpOnly, que este código não lê
    // nem precisa ler. `credentials` é explícito porque o padrão do fetch já
    // mudou uma vez — e se um dia a API sair para outro subdomínio, é esta
    // linha que precisa virar "include" em vez de falhar em silêncio.
    // CADA ABA RENOVA NA PRÓPRIA VAGA. É o caminho que faz o navegador
    // mandar só o cookie desta conta — ver vaga.ts e cookie.go.
    const resposta = await fetch(BASE + caminhoDeRenovacao(), {
      method: "POST",
      credentials: "same-origin",
    });
    if (!resposta.ok) return false;

    const sessao = (await resposta.json()) as {
      token: string;
      caixa?: string;
      vaga?: number;
    };

    // O servidor SEMPRE devolveu `caixa`; este código é que lia só o token e
    // adotava qualquer identidade. Ver renovacaoEhDaMesmaConta.
    if (!renovacaoEhDaMesmaConta(deposito.conta(), sessao.caixa)) {
      // DUAS CAUSAS, duas frases. Dizer "outra conta entrou" quando o que
      // acabou foi a personificação é mentira — e manda a pessoa procurar
      // um culpado que não existe.
      motivo = deposito.personificando()
        ? AVISO_PERSONIFICACAO_EXPIROU
        : AVISO_OUTRA_CONTA;
      // NÃO guarda o token: adotá-lo é exatamente o defeito. Devolver false
      // leva quem chamou ao caminho de sessão perdida, e a aba volta ao
      // login — com a frase acima explicando o que houve.
      deposito.limpar();
      return false;
    }

    deposito.guardarToken(sessao.token);
    if (sessao.caixa) deposito.guardarConta(sessao.caixa);
    // A sessão criada antes das vagas chega aqui pelo caminho antigo e o
    // servidor a MIGRA, devolvendo o número. Guardar fecha a migração: da
    // próxima vez esta aba já renova na vaga dela.
    if (sessao.vaga !== undefined) guardarVagaDaAba(sessao.vaga);
    return true;
  } catch {
    // Falha de REDE não é sessão inválida. Devolver false aqui desloga quem
    // só passou por um túnel — por isso quem chama distingue os dois casos.
    return false;
  }
}

function renovarUmaVezSo(): Promise<boolean> {
  if (!renovacaoEmVoo) {
    renovacaoEmVoo = renovar().finally(() => {
      renovacaoEmVoo = null;
    });
  }
  return renovacaoEmVoo;
}

function montarURL(caminho: string, consulta?: Opcoes["consulta"]): string {
  if (!consulta) return BASE + caminho;
  const p = new URLSearchParams();
  for (const [chave, valor] of Object.entries(consulta)) {
    // Parâmetro ausente é diferente de parâmetro vazio: "?busca=" faria a API
    // filtrar por string vazia em vez de não filtrar.
    if (valor === undefined || valor === null || valor === "") continue;
    p.set(chave, String(valor));
  }
  const texto = p.toString();
  return BASE + caminho + (texto ? "?" + texto : "");
}

async function lerProblema(resposta: Response): Promise<ErroDaAPI> {
  // O TÍTULO NUNCA PODE SER SÓ "Erro".
  //
  // Era `resposta.statusText || "Erro"`, e em HTTP/2 o statusText é SEMPRE
  // vazio — o protocolo não tem frase de motivo. Então todo erro sem corpo
  // JSON virava a palavra "Erro", sozinha, em qualquer tela.
  //
  // Aconteceu de verdade: o nginx devolveu 502 em HTML porque a API havia
  // trocado de IP, e a tela de login mostrou "Erro" e mais nada. Sem o
  // código, quem olha não distingue "senha errada" de "servidor fora do ar"
  // — e os dois pedem ações opostas.
  const padrao: Problema = {
    type: "sobre:em-branco",
    title: resposta.statusText || `Erro ${resposta.status}`,
    status: resposta.status,
  };
  try {
    const corpo = await resposta.json();
    if (corpo && typeof corpo === "object" && "title" in corpo) {
      return new ErroDaAPI({ ...padrao, ...(corpo as Partial<Problema>) });
    }
  } catch {
    /* corpo que não é JSON: fica o padrão */
  }
  return new ErroDaAPI(padrao);
}

async function executar(metodo: Metodo, caminho: string, opcoes: Opcoes): Promise<Response> {
  const cabecalhos: Record<string, string> = { Accept: "application/json" };

  const token = deposito.token();
  if (token) cabecalhos.Authorization = "Bearer " + token;

  let corpo: string | FormData | undefined;
  if (opcoes.corpo instanceof FormData) {
    // SEM Content-Type AQUI, de propósito: o navegador precisa escrever o
    // cabeçalho sozinho, porque só ele conhece a fronteira (`boundary`) que
    // separa as partes. Declarar `multipart/form-data` à mão produz um
    // cabeçalho sem boundary, e o servidor responde que o corpo é inválido.
    corpo = opcoes.corpo;
  } else if (opcoes.corpo !== undefined) {
    cabecalhos["Content-Type"] = "application/json";
    corpo = JSON.stringify(opcoes.corpo);
  }

  try {
    return await fetch(montarURL(caminho, opcoes.consulta), {
      method: metodo,
      headers: cabecalhos,
      body: corpo,
      signal: opcoes.sinal,
      credentials: "same-origin",
    });
  } catch (causa) {
    if (causa instanceof DOMException && causa.name === "AbortError") throw causa;
    throw new ErroDeRede(causa);
  }
}

async function chamar<T>(metodo: Metodo, caminho: string, opcoes: Opcoes = {}): Promise<T> {
  let resposta = await executar(metodo, caminho, opcoes);

  if (resposta.status === 401 && !SEM_RENOVACAO.has(caminho)) {
    if (await renovarUmaVezSo()) {
      resposta = await executar(metodo, caminho, opcoes);
    } else {
      deposito.limpar();
      aoPerderSessao();
      throw await lerProblema(resposta);
    }
  }

  if (!resposta.ok) throw await lerProblema(resposta);

  // 204 e 205 não têm corpo. Tentar ler JSON aqui explode com "Unexpected end
  // of JSON input" — e o contrato usa 204 em todo DELETE.
  if (resposta.status === 204 || resposta.status === 205) return undefined as T;

  if (opcoes.binario) return (await resposta.blob()) as T;
  return (await resposta.json()) as T;
}

export const api = {
  get: <T>(caminho: string, opcoes?: Opcoes) => chamar<T>("GET", caminho, opcoes),
  post: <T>(caminho: string, corpo?: unknown, opcoes?: Opcoes) =>
    chamar<T>("POST", caminho, { ...opcoes, corpo }),
  put: <T>(caminho: string, corpo?: unknown, opcoes?: Opcoes) =>
    chamar<T>("PUT", caminho, { ...opcoes, corpo }),
  patch: <T>(caminho: string, corpo?: unknown, opcoes?: Opcoes) =>
    chamar<T>("PATCH", caminho, { ...opcoes, corpo }),
  // DELETE com corpo não é comum, mas o contrato usa: desativar o 2FA exige a
  // senha atual, para que uma sessão roubada não desligue a proteção.
  delete: <T>(caminho: string, corpo?: unknown, opcoes?: Opcoes) =>
    chamar<T>("DELETE", caminho, corpo === undefined ? opcoes ?? {} : { ...opcoes, corpo }),

  /**
   * Resposta 202 do login significa "faltou o segundo fator", e não é erro.
   * Só o login precisa distinguir, então só ele usa esta porta.
   */
  async postComStatus<T>(caminho: string, corpo?: unknown): Promise<{ status: number; dados: T }> {
    const resposta = await executar("POST", caminho, { corpo });
    if (!resposta.ok) throw await lerProblema(resposta);
    return { status: resposta.status, dados: (await resposta.json()) as T };
  },
};
